import React, { ReactNode, useEffect, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import {
  FormCreationData,
  DeviceType,
  SAVE_FORM_JSON,
  UPDATE_PARENT_FIELDS,
} from "../../../../../stateManager/store/formCreation_v2/types";
import { ElementWrapper } from "./ElementWrapper";
import MicroComponents from "./MicroComponents";
import { ItemTypes } from "../types/ItemTypes";
import { onDrop, handleContainers } from "../helpers";

import { getMargin, getPadding } from "../helpers/generateStyles";
import { containers, canvasElements } from "../constants";

import { getContainerField } from "../helpers/assetHelpers";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../../stateManager/store";
import { useTopElement } from "../hooks/useTopElement";
import { useToast } from "../../../../../helpers/commonFunction";
import { setMultipleSelectionv2 } from "../../../../../stateManager/store/contextMenuv2/action";
import { addWrapperComputedStyles, setSelectedFormItemId, updatedSelectedeElements } from "../../../../../stateManager/store/formCreation_v2/actions";
import { clearSelectedItems } from "../../../../../stateManager/store/contextMenu/action";
import classNames from "classnames";
import { getComputedValues } from "../helpers/formsv3/getComputedValues";
import { HandleContainersV3 } from "../helpers/HandleLayoutComponentsv3";
import { MouseHoverOnTree } from "../../../../../helpers/RxServices";
import { calculatePosition } from "../../../../../utils/badgePosition";
import { onsetClrPicContext } from "../../../../../stateManager/store/common/action";
import { isChildOfFlatList } from "../helpers/getVisibility";
import { avatarData, columnData, emailText, nameText, rowData } from "./templates/FlatlistTemplates";
import { wizardContentInitialState } from "../helpers/wizardHelpers";
import { store } from "../../../../../stateManager";

interface IFlex {
  minHeight?: number;
  deviceType: DeviceType;
  element: FormCreationData;
  isCheckedIn?: Boolean;
  style?: React.CSSProperties;
  children?: ReactNode
  index?: Number;
}

function getFontSize(deviceType: DeviceType): string {
  switch (deviceType) {
    case "Lg":
      return "text-xl";
    case "Md":
      return "text-base";
    default:
      return "text-xs";
  }
}

const horizontalAlignment = (alignX: string): string => {
  switch (alignX) {
    case "start":
      return "justify-self-start"
    case "end":
      return "justify-self-end"
    case "center":
      return "justify-self-center"
    default:
      return ""
  }
}

const verticalAlignment = (alignY: string): string => {
  switch (alignY) {
    case "start":
      return "self-start"
    case "end":
      return "elf-end"
    case "center":
      return "self-center"
    default:
      return ""
  }
}


export const Flex: React.FC<IFlex> = ({ minHeight, isCheckedIn, index, element, deviceType, style: customStyle, children }) => {
  const { id, fields, type, isChildAsset, isAsset } = element;
  const [, setIsTop] = useTopElement();
  const [hoveredElId, setHoveredElId] = useState<string>();
  const { WarningToast } = useToast()
  const { selectedElementId, isCoreV3, components } = useSelector((state: any) => state.formJson);
  const { colorPickerContext } = useSelector((state: any) => state.common);
  
  const preventDroptypesInFlatlist = [
    "FlatList",
    "SnackBar",
    "Modal",
    "CustomModal",
    "FAB",
    "Html",
    "Autocomplete",
    "Tab",
    "TabBar",
    "Markdown",
    "Table",
    "QandA",
    "ImageSlider"
  ]

  const styles = element?.properties?.styles?.[deviceType] || {}
  const paddingStyle = getPadding(styles)
  const gridSpanClasses = `col-span-${styles?.span || (deviceType === "Sm" ? 1 : deviceType === "Md" ? 6 : 12)} row-span-${styles?.rowSpan}`;

  const newStyles: { [key: string]: any } = {
    ...element?.properties?.styles?.common,
    ...element?.properties?.styles?.[deviceType],
    ...customStyle
  }
  const backgroundInfo = {
    backgroundImage: `url(${(newStyles?.backgroundImage?.url) || ''})`,
    backgroundSize: (newStyles?.backgroundImage?.size ) || 'cover',
    backgroundRepeat: (newStyles?.backgroundImage?.repeat) || 'no-repeat',
    
  }

  const { border, borderRadius, borderColor, fontFamily = 'Inherit', backgroundColor, absolute, top, left, bottom, right, zIndex, height, heightMode, width, widthMode, visible, maxHeight } = newStyles;
  const positionGen = absolute ? "absolute" : "relative";
  const visibilityGen = visible ? "block" : "hidden"
  const assetEditModeEnabled = useSelector((state: AppState) => state.formJson.assetEditMode.isEnabled)
  const dispatch = useDispatch();

  const [{ isDragging }, drag] = useDrag({
    item: { type: ItemTypes.ELEMENT, json: { ...element, index } },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: isCheckedIn && !isChildAsset,
  });
  const nonDroppableAssetTypes = ['FormWizard', 'FlatList'];
  const [{ isOverCurrent, canDrop }, drop] = useDrop({
    accept: [
      ItemTypes.ELEMENT,
      ItemTypes.COMPONENT,
      ItemTypes.ASSET,
      ItemTypes.TEMPLATE
    ],
    canDrop: (item: any) => {
      // 1. the source grid itself gets dropped
      if (id === item.json.id) {
        return false
      }

      if (canvasElements.includes(item.json.type)) {
        return false
      }

      if (item.json.type === 'FAB') {
        return false
      }

      // 2. the dragged item is already present in the grid
      if (item.type === ItemTypes.ELEMENT && item.json.parentId === id) {
        return false;
      }

      // 3. asset instance shouldn't drop source when
      // edit mode is turned off
      if (!assetEditModeEnabled && (isChildAsset || isAsset)) {
        return false
      }

      // 4. Prevent dropping preventDroptypesInFlatlist, if it's a child of a flat list.
      const isFlatListChild = isChildOfFlatList(id, components)
      if (isFlatListChild && preventDroptypesInFlatlist.includes(item.json.type)) {
        return false;
      }

      if (nonDroppableAssetTypes.includes(item.json.type) && assetEditModeEnabled) {
        return false
      }
      
      return true;
    },
    collect: (monitor) => {
      return {
        isOverCurrent: monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
      };
    },
    drop: (item: any) => {
      if (isOverCurrent) {
        if (isCheckedIn) {
          let fieldId: any;
          if (item?.json?.type !== 'FormWizard') {
            fieldId = onDrop({ id }, item, deviceType, isCoreV3)
          }
          if (item?.json?.type === "TabBar") {
            let data: any = {
              type: "Component",
              json: {
                type: 'Tab',
                label: 'Tab 1',
                value: "tab 1"
              }
            }
            onDrop({ id: fieldId }, data, deviceType, isCoreV3)
          } else if (item?.json?.type === "FlatList") {
            const rowId: any = onDrop({ id: fieldId }, rowData, deviceType, isCoreV3)
            onDrop({ id: rowId }, avatarData, deviceType, isCoreV3)
            const colId: any = onDrop({ id: rowId }, columnData, deviceType, isCoreV3)
            onDrop({ id: colId }, nameText, deviceType, isCoreV3)
            onDrop({ id: colId }, emailText, deviceType, isCoreV3)
          } else if (item?.json?.type === "FormWizard") {
            onDrop({ id }, {type: ItemTypes.FORMWIZARD, json: {}}, deviceType, isCoreV3);
          }
        } else {
          WarningToast("Please Checkout The Form!!")
        }
      }
    },
  });

  const isActive = isOverCurrent && canDrop;
  const animationClass = isActive ? "drop-active" : canDrop ? "drop-passive" : "";

  useEffect(() => {
    setIsTop(id)
  }, [id, setIsTop])

  useEffect(() => {
    if (selectedElementId === id && (components[selectedElementId]?.type === "Row" || components[selectedElementId]?.type === "Column")) {
      const childElement = document.getElementById(`${type}-${id}`)
      if (childElement) {
        const computedStyles = window.getComputedStyle(childElement);
        const camelCaseStyles: any = {};

        for (let i = 0; i < computedStyles.length; i++) {
          const prop = computedStyles[i];
          const value = computedStyles.getPropertyValue(prop);
          const camelProp = kebabToCamel(prop);
          camelCaseStyles[camelProp] = value;
        }
        const selectedStyles = {
          width: camelCaseStyles.width,
          height: camelCaseStyles.height,
          marginTop: camelCaseStyles.marginTop,
          marginBottom: camelCaseStyles.marginBottom,
          marginLeft: camelCaseStyles.marginLeft,
          marginRight: camelCaseStyles.paddingRight,
          position: camelCaseStyles.position,
          lineHeight: camelCaseStyles.lineHeight,
          backgroundColor: computedStyles.getPropertyValue("background-color"),
          borderColor: computedStyles.getPropertyValue("border-color")
        };
        dispatch(addWrapperComputedStyles(selectedStyles))
      }
    }
  }, [selectedElementId])

  useEffect(() => {
    const subscription = MouseHoverOnTree.getMessage().subscribe(function (e) {
      setHoveredElId(e);
    });
    if (!subscription) {
      MouseHoverOnTree.getMessage();
    }
    return () => {
      subscription.unsubscribe();
    };
  }, [hoveredElId]);

  const marginStyle = getMargin(newStyles);
  const handleSelect = (e: React.MouseEvent<HTMLDivElement>, id: any) => {
    e.stopPropagation();
    if (e.shiftKey && !isChildAsset) {
      dispatch(setMultipleSelectionv2(true));
      dispatch(updatedSelectedeElements(id));
    } else {
      dispatch(setMultipleSelectionv2(false));
      dispatch(
        setSelectedFormItemId({
          id,
          isAssetInstanceSelected: isAsset || isChildAsset,
        })
      );
      dispatch(clearSelectedItems())
    }
  };

  const handleRightClick = async (e: React.MouseEvent<HTMLDivElement>, id: any) => {
    e.stopPropagation();
    const existingWrapper = document.getElementById("wrapper");
    existingWrapper && await existingWrapper.remove();
    await dispatch(
      setSelectedFormItemId({
        id,
        isAssetInstanceSelected: isAsset || isChildAsset,
      })
    );
    await dispatch(clearSelectedItems());
  };

  let flexShrink = 0, flexGrow = 0
  if (newStyles?.flexShrink) {
    flexShrink = parseInt(newStyles.flexShrink || 0)
  }
  if (newStyles?.flexGrow) {
    flexGrow = parseInt(newStyles.flexGrow || "0")
  }

  const getHeight = (): string => {
    let returnVal = "82px";
    if (heightMode === 'auto') {
      returnVal = "auto"
    } else if (heightMode === 'min-content') {
      returnVal = "min-content"
    } else if (heightMode === 'max-content') {
      returnVal = "max-content"
    } else if (heightMode === '%' || heightMode === 'px') {
      returnVal = `${height}${heightMode}`
    } else if (type === "Column") {
      returnVal = "100%"
    } else if (minHeight && (type === "CustomModal" || !fields.length)) {
      returnVal = `${minHeight}px`
    } 
    return returnVal
  }

  const getWidth = (): string => {
    if (widthMode === 'auto') {
      return "fit-content"
    } else if (widthMode === 'min-content') {
      return "min-content"
    } else if (widthMode === 'max-content') {
      return "max-content"
    } else if (width) {
      return `${width}${newStyles.widthMode}`;
    } else if (type === "CustomModal") {
      return "100%"
    } else if(type === "Row") {
      return "100%"
    }
    return ""
  }
  const isEmpty = fields && fields.length === 0;
  const flexWrapperStyles: React.CSSProperties = {
    ...marginStyle,
    ...backgroundInfo,
    fontFamily,
    flexShrink,
    flexGrow,
    width: getWidth(),
    alignSelf: newStyles.alignY ? newStyles.alignY : "",
    height: getHeight(),
    borderRadius: borderRadius ? `${borderRadius}px` : "0px",
    top: `${top ? `${top}px` : ""}`,
    left: `${left ? `${left}px` : ""}`,
    bottom: `${bottom ? `${bottom}px` : ""}`,
    right: `${right ? `${right}px` : ""}`,
    maxHeight: maxHeight || "unset",
    cursor: 'pointer',
    opacity: isDragging ? 0.4 : 1,
    backgroundColor: hoveredElId === id ? "#EFF0F4 ": backgroundColor ? `${backgroundColor}` : isEmpty ? '#f1f1f1' : '#fff',
    maxWidth: "100%"
  }
 
  const style = element?.properties?.styles || {};
  const flexWrapperChildStyles: React.CSSProperties = {
    ...paddingStyle,
    display: 'flex',
    flexDirection: type === 'Row' ? 'row' : type === 'Column' ? 'column' : 'column',
    justifyContent: styles?.flexDistribute,
    alignItems: styles?.flexAlign,
    flexWrap: styles?.flexWrap ? 'wrap' : undefined,
    gap: styles?.gap ? `${styles?.gap}px` : '',
    minWidth: '1px',
    height: (type === "Column" || height)? "100%" : "82px",
    minHeight: getHeight(),
    borderWidth: border ? `${border}px` : undefined,
    borderColor: borderColor ? `${borderColor}` : "#fff",
    overflowX: newStyles?.overflowX ? newStyles?.overflowX : "hidden",
    overflowY: newStyles?.overflowY ? newStyles?.overflowY : "hidden",
    flexGrow: style[deviceType]?.flexGrow ? Number(style[deviceType]?.flexGrow) : "unset",
    flexShrink: style[deviceType]?.flexShrink ? Number(style[deviceType]?.flexShrink) : 0,
    borderRadius: borderRadius ? `${borderRadius}px` : "0px",
    zIndex: zIndex ? zIndex : "unset"
  }

  const flexStyle: React.CSSProperties = {
    fontFamily: fontFamily,
    padding: `${fields.length === 0 ? "10px" : ""}`,
    background: `${fields.length === 0 ? "rgba(217, 217, 217, .1)" : "none"}`
  }

  function kebabToCamel(kebab: any) {
    return kebab.replace(/-([a-z])/g, (match: any, letter: any) => letter.toUpperCase());
  }

  const combinedClassName = element?.properties?.config?.classNameTags?.join(" ") || [];
  return (
    <>
      {
        isCoreV3 ?
          <div
            ref={ref => drag(drop(ref))}
            id={`${type}-${id}`}
            className={`${type}-wrapper ${combinedClassName} ${positionGen} ${visibilityGen} ${gridSpanClasses} ${horizontalAlignment(newStyles?.alignX)} ${verticalAlignment(newStyles?.alignY)}`}
            style={flexWrapperStyles}>
            <div
              onClick={(e) => handleSelect(e, id)}
              onContextMenu={(e) => {
                dispatch(onsetClrPicContext({ ...colorPickerContext, visibility: false }))
                e.preventDefault();
                handleRightClick(e, id);
              }}
              ref={ref => drop(ref)}
              onMouseOver={(e) => {
                e.stopPropagation();
                MouseHoverOnTree.sendMessage(id);
              }}
              onMouseLeave={() => {
                MouseHoverOnTree.sendMessage(null);
              }}
              className={`${type}-wrapperPadding ${horizontalAlignment(newStyles?.alignX)} ${verticalAlignment(newStyles?.alignY)} ${animationClass}`}
              id={`${id}`}
              key={`${type}-${id}`}
              style={{...flexWrapperChildStyles, ...backgroundInfo}}
            >{children ? children : null}
              {fields?.map((fieldId, index) => {
                const elements = getContainerField(fieldId, element);
                if (!elements) {
                  return null;
                }
                const { component, parentComponent } = elements;
                return (
                  <>
                    {
                      containers.includes(component.type) ? (
                        <HandleContainersV3 key={index} isCheckedIn={isCheckedIn} element={component} deviceType={deviceType} index={index} />
                      ) : (
                        <MicroComponents key={fieldId} index={index} element={component} deviceType={deviceType} />
                      )
                    }
                  </>
                );
              })}
            </div>
          </div>
          :
          <div style={flexStyle}>
            {selectedElementId === id && type !== "FAB" && type !== "A8Modal" && <p
              data-test-id={id}
              id={`tag_${id}`}
              style={{ ...marginStyle }}
              className={`${combinedClassName}` && type === "SnackBar" ? "fixed bg-palered text-sm text-white px-1 rounded-sm bottom-14 z-50 left-4 mt-3" : `absolute ${getFontSize(deviceType)}bg-palered flex items-center text-white px-1 rounded h-6 bottom-0 z-50 left-0`}>
              {id}</p>
            }
            <div
              ref={ref => drop(ref)}
              className={animationClass}
              id={`${type}-${id}`}
              style={{
                ...paddingStyle,
                ...backgroundInfo,
                display: 'flex',
                flexDirection: type === 'Row' ? 'row' : type === 'Column' ? 'column' : undefined,
                justifyContent: styles?.flexDistribute,
                alignItems: styles?.flexAlign,
                flexWrap: styles?.flexWrap ? 'wrap' : undefined,
                gap: styles?.gap ? `${styles?.gap}px` : '10px',
                minWidth: '30px',
                height: fields.length === 0 ? "82px" : "auto",
                minHeight: styles?.height ? `${styles.height}${styles.heightMode}` : "30px",
                borderWidth: border ? `${border}px` : undefined,
                borderRadius: borderRadius ? `${borderRadius}px` : undefined,
                borderColor: borderColor ? `${borderColor}` : undefined,
                backgroundColor:hoveredElId === id ? "#EFF0F4": backgroundColor ? `${backgroundColor}` : isEmpty ? '#f1f1f1' : '#fff',
              }}
            >
              {fields?.map((fieldId, index) => {
                const elements = getContainerField(fieldId, element)
                if (!elements) {
                  return null
                }
                const { component, parentComponent } = elements
                return (
                  <ElementWrapper isCheckedIn={isCheckedIn} key={component.id} element={component} index={index} parentElement={parentComponent}>
                    <div>
                      {containers.includes(component.type) ? (
                        handleContainers({ isCheckedIn, element: component, deviceType })
                      ) : (
                        <MicroComponents element={component} deviceType={deviceType} />
                      )}
                    </div>
                  </ElementWrapper>
                );
              })}
              {fields.length && isActive ? <div style={{ height: "60px", width: "100%" }} /> : null}
            </div>
          </div>
      }
    </>
  );
};
export const FlexWithBadge = (props: IFlex) => {
  const { element: { properties: { styles: { common }, config: { enableBadge } } } } = props;
  const { badge: { position, customHorizontal, customVertical, badgeBindBadge, WidthHeigthh = 25, WidthHeigthw = 25, defaultValue, background, textColor, badgeFontSize, badgeBorderRadius, borderWidth, badgeBorderColor } }= common || { badge: {} } ;
  let pos = calculatePosition(position, { customHorizontal, customVertical });

  const badgeStyle: React.CSSProperties = {
    background: background,
    borderRadius: badgeBorderRadius,
    borderColor: badgeBorderColor,
    borderWidth: `${borderWidth}px`,
    fontSize: `${badgeFontSize}px`,
    color: textColor,
    width: WidthHeigthw ? `${WidthHeigthw}px` : "25px",
    height: WidthHeigthh ? `${WidthHeigthh}px` : "25px",
    zIndex: 999
  }
  return <Flex {...props}>
    {enableBadge && <div className={`absolute ${pos} flex items-center justify-center`} style={badgeStyle} >{defaultValue}</div>}
  </Flex>
}